import { create } from "zustand";

/**
 * "Besprochen"-Zustand je Ticket, pro Kundenabteilung in localStorage
 * gespeichert unter `msc-cv-reviewed:<objectKey>`.
 *
 * v2: je Ticket wird der ABHAK-ZEITPUNKT gespeichert (ISO) — Grundlage für den
 * "Neu seit letztem Termin"-Marker (Ticket-Update nach dem letzten Abhaken).
 * Migration vom v1-Format (Array der Keys): Zeitpunkt = jetzt, damit nach dem
 * Deploy nicht alles auf einmal als "Neu" aufleuchtet.
 *
 * Port aus der Hub-App (`jira-customer-view/frontend/src/store/reviewed.ts`);
 * einzige Abweichung: Storage-Schlüsselpräfix `msc-cv-reviewed:` statt
 * `reviewed:`.
 */

const storageKey = (objectKey: string) => `msc-cv-reviewed:${objectKey}`;

type ReviewedMap = Record<string, string>; // ticketKey -> reviewedAtIso

function load(objectKey: string): ReviewedMap {
  try {
    const raw = localStorage.getItem(storageKey(objectKey));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // v1: ["MS-1", ...] -> migriere mit "jetzt"
    if (Array.isArray(parsed)) {
      const now = new Date().toISOString();
      const map: ReviewedMap = {};
      for (const k of parsed) if (typeof k === "string") map[k] = now;
      return map;
    }
    if (parsed && typeof parsed === "object" && (parsed as { v?: unknown }).v === 2) {
      const items = (parsed as { items?: unknown }).items;
      return items && typeof items === "object" ? (items as ReviewedMap) : {};
    }
  } catch {
    /* ignore corrupt / unavailable storage */
  }
  return {};
}

function persist(objectKey: string, items: ReviewedMap): void {
  try {
    localStorage.setItem(storageKey(objectKey), JSON.stringify({ v: 2, items }));
  } catch {
    /* ignore quota / unavailable storage */
  }
}

interface ReviewedState {
  /** objectKey, das aktuell in den Store geladen ist. */
  objectKey: string | null;
  /** Abhak-Zeitpunkt je Ticket (ISO). */
  reviewedAt: ReviewedMap;
  loadCustomer: (objectKey: string | null) => void;
  toggle: (ticketKey: string) => void;
  /** Abhaken OHNE Toggle (für Auto-Haken beim Weiterblättern). */
  markReviewed: (ticketKey: string) => void;
  isReviewed: (ticketKey: string) => boolean;
}

export const useReviewedStore = create<ReviewedState>((set, get) => ({
  objectKey: null,
  reviewedAt: {},
  loadCustomer: (objectKey) => {
    if (objectKey === get().objectKey) return;
    const items = objectKey ? load(objectKey) : {};
    set({ objectKey, reviewedAt: items });
  },
  toggle: (ticketKey) =>
    set((s) => {
      if (!s.objectKey) return s;
      const items = { ...s.reviewedAt };
      if (items[ticketKey]) delete items[ticketKey];
      else items[ticketKey] = new Date().toISOString();
      persist(s.objectKey, items);
      return { reviewedAt: items };
    }),
  markReviewed: (ticketKey) =>
    set((s) => {
      if (!s.objectKey || s.reviewedAt[ticketKey]) return s;
      const items = { ...s.reviewedAt, [ticketKey]: new Date().toISOString() };
      persist(s.objectKey, items);
      return { reviewedAt: items };
    }),
  isReviewed: (ticketKey) => ticketKey in get().reviewedAt,
}));

/**
 * "Neu seit letztem Termin": das Ticket wurde nach dem letzten Abhaken
 * aktualisiert — oder es wurde nie besprochen und ist nach dem jüngsten
 * Abhak-Zeitpunkt des Kunden entstanden. Ohne jede Abhak-Historie gibt es
 * keinen Referenzpunkt -> nie "Neu" (vermeidet Alles-leuchtet beim Erstlauf).
 */
export function isNewSinceLastMeeting(
  ticket: { key: string; updatedIso: string; createdIso: string },
  reviewedAt: ReviewedMap,
): boolean {
  const own = reviewedAt[ticket.key];
  if (own) return ticket.updatedIso > own;
  const stamps = Object.values(reviewedAt);
  if (stamps.length === 0) return false;
  const latest = stamps.reduce((a, b) => (a > b ? a : b));
  return ticket.createdIso > latest;
}
